<template>
    <div class="form-group col-4">
        <label for="model" class="control-label">Model</label>
        <div v-if="loadError" class="alert alert-warning py-2 mb-2">
            <small>{{ loadError }}</small>
        </div>
        <div v-else-if="missingModelWarning" class="alert alert-warning py-2 mb-2">
            <small>
                The previously saved model <code>{{ missingModelWarning }}</code>
                is no longer available for this {{ modelProviderAccountName }} account.
                Pick a replacement before saving.
            </small>
        </div>
        <select id="model" name="model" v-model="localModel"
                :disabled="!!loadError"
                :class="['form-control', 'model-select', { 'is-invalid': error }]">
            <optgroup v-if="missingModelWarning" label="Unavailable">
                <option :value="missingModelWarning" disabled>
                    ⚠ {{ missingModelWarning }} (no longer available)
                </option>
            </optgroup>
            <option v-for="m in models" :key="m.id" :value="m.id">{{ m.label }}</option>
        </select>
        <span v-if="error" class="invalid-feedback"><strong>{{ error }}</strong></span>
        <div v-if="selectedLegacyInfo" class="alert alert-warning py-2 mt-2 mb-0">
            <small>
                <font-awesome-icon :icon="warningIcon" class="mr-1" />
                <strong>Legacy model</strong> — end of life:
                {{ formatEolDate(selectedLegacyInfo.end_of_life_time) }}
            </small>
        </div>
    </div>
</template>

<script>
import {faExclamationTriangle} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

export default {
    name: 'ModelSelect',
    components: {FontAwesomeIcon},
    props: {
        modelValue: {type: String, default: ''},
        provider: {type: String, default: 'openai'},
        initialModels: {type: Array, default: () => []},
        initialError: {type: String, default: null},
        initialModelMissing: {type: Boolean, default: false},
        error: {type: String, default: null},
    },
    emits: ['update:modelValue'],
    data() {
        return {
            models: this.normalize(this.initialModels),
            localModel: this.modelValue,
            loadError: this.initialError,
            // Track the missing model id so we can render it as a disabled option
            // and warn the user until they pick a real replacement.
            missingModelWarning: this.initialModelMissing ? this.modelValue : null,
            warningIcon: faExclamationTriangle,
            modelRequestId: 0,
        };
    },
    computed: {
        modelProviderAccountName() {
            return this.provider === 'bedrock' ? 'AWS' : 'OpenAI';
        },
        // When AWS has marked the currently-selected model as LEGACY, the
        // discovery service includes a `lifecycle` block carrying status + EOL.
        // OpenAI models don't carry lifecycle data, so this is always null for
        // OpenAI assistants.
        selectedLegacyInfo() {
            const m = this.models.find(m => m.id === this.localModel);
            return m?.lifecycle?.status === 'LEGACY' ? m.lifecycle : null;
        },
    },
    watch: {
        modelValue(value) {
            this.localModel = value;
        },
        localModel(value) {
            // Once the user picks a different (valid) model, dismiss the missing warning.
            if (this.missingModelWarning && value !== this.missingModelWarning) {
                this.missingModelWarning = null;
            }
            this.$emit('update:modelValue', value);
        },
        async provider(provider) {
            const requestId = ++this.modelRequestId;

            try {
                const response = await fetch(`/assistants-models?provider=${encodeURIComponent(provider)}`, {
                    headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
                });
                if (requestId !== this.modelRequestId) return;

                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    if (requestId !== this.modelRequestId) return;

                    this.models = [];
                    this.loadError = body.error || `Could not load ${provider} models.`;
                    return;
                }
                const payload = await response.json();
                if (requestId !== this.modelRequestId) return;

                this.models = this.normalize(payload);
                this.loadError = null;
                // Provider switch is user-initiated — auto-pick the first available model
                // rather than warning. The "missing model" warning only applies to the
                // original-load case where the saved model dropped out of the catalog.
                this.missingModelWarning = null;
                if (!this.models.some(m => m.id === this.localModel)) {
                    this.localModel = this.models[0]?.id || '';
                }
            } catch (error) {
                if (requestId !== this.modelRequestId) return;

                this.models = [];
                this.loadError = `Could not load ${provider} models. Contact your system administrator.`;
                console.error('Failed to load models:', error);
            }
        },
    },
    methods: {
        normalize(list) {
            return (Array.isArray(list) ? list : []).map(item => {
                if (typeof item === 'string') {
                    return {id: item, label: item, lifecycle: null};
                }
                return {
                    id: item.id,
                    label: item.label ?? item.id,
                    lifecycle: item.lifecycle ?? null,
                };
            });
        },
        formatEolDate(iso) {
            if (!iso) return 'date unknown';
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return iso;
            return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
        },
    },
};
</script>

<style scoped>

.model-select {
    color-scheme: light;
}

.model-select optgroup {
    color: #000;
    background-color: #fff;
    font-weight: 600;
}

.model-select option {
    color: #000;
    background-color: #fff;
}
</style>
